import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, Mail, Save, Trash2, Paperclip } from "lucide-react";
import { Card, field, cfield, btn, fmtTs } from "./ui";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["new", "contacted", "booked", "archived"];
const TEMPLATES = [
  ["thanks", "Thanks for reaching out"],
  ["followup", "Checking in / follow-up"],
  ["quote", "Next steps / quote ready"],
];

export const LeadsTab = ({ auth }) => {
  const [leads, setLeads] = useState([]);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [notesDraft, setNotesDraft] = useState({});
  const [emailFor, setEmailFor] = useState(null);
  const [tpl, setTpl] = useState("thanks");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);

  const load = () => axios.get(`${API}/leads`, auth).then((r) => setLeads(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return [...leads]
      .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))
      .filter((l) => typeFilter === "all" || l.type === typeFilter)
      .filter((l) => !q || [l.name, l.email, l.business, l.message, l.brand_name].some((v) => (v || "").toLowerCase().includes(q)));
  }, [leads, search, typeFilter]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/leads/${id}`, { status }, auth);
      setLeads((s) => s.map((l) => (l.id === id ? { ...l, status } : l)));
    } catch { toast.error("Could not update status."); }
  };

  const saveNotes = async (id) => {
    try {
      await axios.patch(`${API}/admin/leads/${id}`, { notes: notesDraft[id] ?? "" }, auth);
      setLeads((s) => s.map((l) => (l.id === id ? { ...l, notes: notesDraft[id] ?? "" } : l)));
      toast.success("Note saved.");
    } catch { toast.error("Could not save note."); }
  };

  const del = async (id, name) => {
    if (!window.confirm(`Delete ${name || "this lead"} permanently? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/leads/${id}`, auth);
      setLeads((s) => s.filter((l) => l.id !== id));
      toast.success("Lead deleted.");
    } catch { toast.error("Delete failed."); }
  };

  const sendFollowup = async (id) => {
    setSending(true);
    try {
      await axios.post(`${API}/admin/leads/${id}/email`, { template: tpl, note }, auth);
      toast.success("Follow-up email sent.");
      setEmailFor(null); setNote("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed.");
    } finally { setSending(false); }
  };

  const exportCsv = async () => {
    try {
      const r = await axios.get(`${API}/admin/leads.csv`, { ...auth, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "stax-leads.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed."); }
  };

  const typeLabel = (t) => ({ contact: "Inquiry", brand_kit: "Brand Kit", freebie: "Freebie" }[t] || t);

  return (
    <div className="space-y-6" data-testid="leads-tab">
      <Card
        title={`Leads (${shown.length})`}
        color="bg-staxcyan"
        testid="leads-card"
        action={
          <button data-testid="leads-export-csv" onClick={exportCsv} className={`${btn} bg-white text-ink`}>
            <Download size={15} /> Export CSV
          </button>
        }
      >
        <div className="mb-5 flex flex-col sm:flex-row gap-3">
          <input
            data-testid="lead-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, business..."
            className={field}
          />
          <select data-testid="lead-filter-type" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className={`${field} sm:w-48`}>
            <option value="all">All types</option>
            <option value="contact">Inquiries</option>
            <option value="brand_kit">Brand Kit</option>
            <option value="freebie">Freebie</option>
          </select>
        </div>

        <div className="space-y-3">
          {shown.length === 0 && <p className="font-mono2 text-xs uppercase tracking-widest text-ink/40">No leads match.</p>}
          {shown.map((l) => (
            <div key={l.id} data-testid={`lead-card-${l.id}`} className="border-2 border-ink/15 bg-white p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-display uppercase text-lg leading-tight">{l.name || l.brand_name || "Unnamed"}</p>
                  <p className="text-sm text-ink/60 truncate">{l.email}{l.business ? ` · ${l.business}` : ""}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="border-2 border-ink bg-staxyellow px-2 py-1 font-mono2 text-[10px] font-bold uppercase tracking-widest">
                    {typeLabel(l.type)}
                  </span>
                  <select
                    data-testid={`lead-status-${l.id}`}
                    value={l.status || "new"}
                    onChange={(e) => setStatus(l.id, e.target.value)}
                    className={cfield}
                  >
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <button
                    data-testid={`lead-delete-${l.id}`}
                    onClick={() => del(l.id, l.name)}
                    aria-label="Delete lead"
                    className="border-2 border-ink p-2 hover:bg-staxmagenta hover:text-white transition-colors"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              {l.message && <p className="mt-2 text-sm text-ink/70 leading-relaxed">{l.message}</p>}
              {l.attachment?.id && (
                <a
                  data-testid={`lead-attachment-${l.id}`}
                  href={`${API}/files/${l.attachment.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-flex items-center gap-1.5 border-2 border-ink bg-staxcyan px-2.5 py-1 font-mono2 text-[10px] font-bold uppercase tracking-widest text-ink hover:bg-staxyellow transition-colors"
                >
                  <Paperclip size={11} /> {l.attachment.original_filename || "Attachment"}
                </a>
              )}
              {l.type === "brand_kit" && (
                <p className="mt-2 font-mono2 text-[10px] uppercase tracking-widest text-ink/50">
                  {l.brand_name} · {l.personality} · {l.palette} · {l.typography} · {l.logo_style}
                </p>
              )}
              <p className="mt-1 font-mono2 text-[10px] uppercase tracking-widest text-ink/35">
                {fmtTs(l.created_at)}
              </p>
              <div className="mt-3 flex gap-2">
                <input
                  data-testid={`lead-notes-${l.id}`}
                  value={notesDraft[l.id] ?? l.notes ?? ""}
                  onChange={(e) => setNotesDraft((d) => ({ ...d, [l.id]: e.target.value }))}
                  placeholder="Private notes..."
                  className={cfield}
                />
                <button data-testid={`lead-notes-save-${l.id}`} onClick={() => saveNotes(l.id)} aria-label="Save note" className={`${btn} bg-white text-ink`}>
                  <Save size={14} />
                </button>
                {l.email && (
                  <button
                    data-testid={`lead-email-toggle-${l.id}`}
                    onClick={() => setEmailFor(emailFor === l.id ? null : l.id)}
                    className={`${btn} bg-staxmagenta text-white`}
                  >
                    <Mail size={14} /> Reply
                  </button>
                )}
              </div>
              {emailFor === l.id && (
                <div className="mt-3 border-2 border-dashed border-ink/30 p-3 space-y-2">
                  <select data-testid={`lead-email-template-${l.id}`} value={tpl} onChange={(e) => setTpl(e.target.value)} className={cfield}>
                    {TEMPLATES.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
                  </select>
                  <textarea
                    data-testid={`lead-email-note-${l.id}`}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Your reply — this becomes the entire email body (template text is only used if you leave this blank)"
                    className={`${cfield} min-h-[70px] resize-none`}
                  />
                  <button data-testid={`lead-email-send-${l.id}`} onClick={() => sendFollowup(l.id)} disabled={sending} className={`${btn} bg-staxcyan text-ink`}>
                    {sending ? "Sending..." : "Send Email"}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};
